import { useEffect, useRef, useState } from 'react';

import { MINDSET_IMAGE_URL } from '@/constants';

import {
  setMindsetBackgroundLoading,
  useMindsetBackgroundState,
} from './store';

import type { Mindset } from '@/types';
import type { FC } from 'react';

const FADE_MS = 600;

const imageStyle = (mindset: Mindset) => ({
  backgroundImage: `url("${MINDSET_IMAGE_URL}${mindset}.jpg")`,
});

interface MindsetBackgroundProps {
  mindset: Mindset;
}

// Full-bleed mindset image that crossfades when the mindset changes. The current
// image stays put (slightly dimmed) until the next one has loaded, then the new one
// fades in on top, so there's immediate feedback and never a flash of empty colour.
const MindsetBackground: FC<MindsetBackgroundProps> = ({ mindset }) => {
  const { isLoading, requested } = useMindsetBackgroundState();
  const target = requested ?? mindset;

  const [base, setBase] = useState(target);
  const [incoming, setIncoming] = useState<Mindset | null>(null);
  const incomingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (target === base && !incoming) return undefined;
    if (target === (incoming ?? base)) return undefined;

    let cancelled = false;
    let settled = false;
    setMindsetBackgroundLoading(true);

    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      settled = true;
      // A second pick before the first fade finished: lock in the first one.
      if (incoming) setBase(incoming);
      setIncoming(target);
      setMindsetBackgroundLoading(false);
    };
    image.onerror = () => {
      if (cancelled) return;
      settled = true;
      setMindsetBackgroundLoading(false);
    };
    image.src = `${MINDSET_IMAGE_URL}${target}.jpg`;

    return () => {
      cancelled = true;
      // The preload was cancelled or became obsolete (e.g. the selection rolled back to
      // the current image). Clear the shared loading flag so the veil and pulse don't
      // stay on; a newer preload sets it again right after this cleanup.
      if (!settled) setMindsetBackgroundLoading(false);
    };
  }, [target, base, incoming]);

  // Fade the incoming layer in with the Web Animations API (an explicit 0 -> 1 animation
  // is reliable even when the layer mounts and animates in the same frame), then promote
  // it to the base layer. Reduced-motion users get an instant swap.
  useEffect(() => {
    const layer = incomingRef.current;
    if (!incoming || !layer) return undefined;

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const fade = layer.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: reduceMotion ? 0 : FADE_MS,
      easing: 'ease-in-out',
      fill: 'forwards',
    });
    fade.onfinish = () => {
      setBase(incoming);
      setIncoming(null);
    };

    return () => {
      fade.onfinish = null;
      fade.cancel();
    };
  }, [incoming]);

  // Never leave the "loading" signal on if this unmounts mid-load.
  useEffect(() => () => setMindsetBackgroundLoading(false), []);

  return (
    <>
      <div
        className="pointer-events-none fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
        style={imageStyle(base)}
        aria-hidden
      />
      {incoming && (
        <div
          key={incoming}
          ref={incomingRef}
          className="pointer-events-none fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
          style={{ ...imageStyle(incoming), opacity: 0 }}
          aria-hidden
        />
      )}
      <div
        className="pointer-events-none fixed inset-0 -z-10 bg-black transition-opacity duration-300 motion-reduce:transition-none"
        style={{ opacity: isLoading ? 0.2 : 0 }}
        aria-hidden
      />
    </>
  );
};

export default MindsetBackground;
